import { createNativeStackNavigator, NativeStackScreenProps } from '@react-navigation/native-stack';
import * as React from 'react';
import { AccountListScreen } from '@/features/account-management/screens/list/AccountList.screen';
import { AccountDetailsNavigator } from '@/features/account-management/screens/details/accountDetails.navigator';
import { ViewAllCardScreen } from '@/features/account-management/screens/view-all-card/ViewAllCard.screen';
import { LockCardNavigator } from '@/features/account-management/screens/lock-card/lockCard.navigator';
import { SetCardLimitNavigator } from '@/features/account-management/screens/set-card-limit/setCardLimit.navigator';
import { ChangeATMPINNavigator } from '@/features/account-management/screens/change-atm-pin/changeATMPIN.navigator';
import { ReplaceCardScreen } from '@/features/account-management/screens/replace-card/ReplaceCard.screen';
import { UpdateMobileNoNavigator } from '@/features/account-management/screens/update-mobile-no/updateMobileNo.navigator';
import { AccountTransactionHistoryScreen } from '@/features/account-management/screens/details/AccountTransactionHistory.screen';
import { AccountTransactionDetailScreen } from '@/features/account-management/screens/details/AccountTransactionDetail.screen';
import { CardTransactionHistoryScreen } from '@/features/account-management/screens/details/CardTransactionHistory.screen';
import { CardDetailNavigator } from '@/features/account-management/screens/details/cardDetail.navigator';
import { CardDetailInfoWebScreen } from '@/features/account-management/screens/details/CardDetailInfoWeb.screen';
import { TopUpNavigator } from '@/features/account-management/screens/top-up/topUp.navigator';
import { WithdrawNavigator } from '@/features/account-management/screens/withdraw/withdraw.navigator';
import { NewAccountNavigator } from '@/features/account-management/screens/new-account/newAccount.navigator';
import { IssueCardNavigator } from '@/features/account-management/screens/issue-card/issueCard.navigator';
import { CardActivationNavigator } from '@/features/account-management/screens/card-activation/cardActivation.navigator';
import { CancelCardNavigator } from '@/features/account-management/screens/cancel-card/cancelCard.navigator';
import { SuspendCardNavigator } from '@/features/account-management/screens/suspend-card/suspendCard.navigator';
import { DigitalDepositNavigator } from '@/features/account-management/screens/digital-deposit/digitalDeposit.navigator';
import { AccountsTransfersNavigator } from '@/features/account-management/screens/accounts-transfers/accountsTransfers.navigator';
import {
  BottomSheetFilter as ListBottomSheetFilter,
  BottomSheetFilterProps as ListBottomSheetFilterProps
} from '@/features/account-management/screens/list/BottomSheetFilter';
import {
  BottomSheetDatePicker,
  BottomSheetDatePickerProps
} from '@/features/account-management/screens/bottomSheetDatePicker/BottomSheetDatePicker';
import { useContext } from 'react';
import { LayoutContext } from 'contexts/LayoutContext';
import { Card, Xmetadata } from '@utx/utx-access-client';
import { EarnStackRoutes } from '@/features/earn';
import { AccountSelectorNativeModal } from '@/design-system/components/accountSelector/AccountSelectorNativeModal';
import { phoneNumberProp } from '@/features/account-management/screens/details/hooks/useCard.hook';
import { CardWithLimitProp } from '@/features/account-management/types';
import { BillPaymentUnionPay } from '@/features/bill-payment/payment.navigator';
import { UnionPayNavigator } from '@/features/bill-payment/screens/union-pay/AccountUnionPay.navigator';
import { DownloadTransactionReportNavigator } from '@/features/account-management/screens/download-transaction-report/DownloadTransactionReport.navigator';
import { TransactionInfoScreen } from '@/features/account-management/screens/details/TransactionInfo.screen';
import { FixedLimitNavigator } from '@/features/account-management/screens/fixed-limit/fixedLimit.navigator';
import { UpdateCardAccountKycInfo } from '@/features/account-management/screens/Kyc-info/KycInfoUpdate';

export enum AccountManagementStackRoutes {
  List = 'account-management-list',
  Details = 'account-details',
  CardDetail = 'account-card-detail',
  CardDetailInfo = 'account-card-detail-info',
  ViewAllCard = 'account-view-all-card',
  LockCard = 'account-lock-card',
  SetCardLimit = 'account-set-card-lmit',
  ChangeATMPIN = 'account-change-atm-pin',
  ReplaceCard = 'account-replace-card',
  UpdateMobileNo = 'account-update-mobile-no',
  ListBottomSheetFilter = 'list-bottom-sheet-filter',
  BottomSheetDatePicker = 'account-buttom-sheet-date-picker',
  AccountTransactionHistory = 'account-transaction-history',
  AccountTransactionDetail = 'account-transaction-detail',
  CardTransactionHistory = 'card-transaction-history',
  TopUp = 'card-top-up',
  Withdraw = 'card-withdraw',
  NewAccount = 'card-new-account',
  IssueCard = 'card-issue-card',
  CardActivation = 'account-card-activation',
  CancelCard = 'account-cancel-card',
  SuspendCard = 'account-suspend-card',
  UnionPay = 'account-payment-union-pay',
  DigitalDeposit = 'account-digital-deposit',
  AccountsTransfers = 'account-accounts-transfers',
  DownloadTransactionReport = 'download-transaction-report',
  Xmetadata = 'transaction-xmetadata',
  FixedLimit = 'cardaccount-fixed-limit',
  UpdateKycInfo = 'Update-kyc-info'
}

export type AccountManagementStackParamList = {
  [AccountManagementStackRoutes.List]: undefined;
  [AccountManagementStackRoutes.Details]: {
    screen?: string;
    account: string;
    tab?: number;
    record: any;
    flag?: string;
    options?: { [key: string]: any };
  };
  [AccountManagementStackRoutes.CardDetail]: {
    account: string;
    tab?: number;
    record?: Card & { currency?: string; limit?: number };
    funcLockCard?: (cardId: string, onSuccess?: (data: any) => void, onError?: (error: any) => void) => void;
    funcUnlockCard?: (cardId: string, onSuccess?: (data: any) => void, onError?: (error: any) => void) => void;
    funcSetCardLimit?: (
      cardId: string,
      atmLimit?: number,
      purchaseLimit?: number,
      onSuccess?: (data: any) => void,
      onError?: (error: any) => void
    ) => void;
    funcChangeCardPin?: (cardId: string, pin: string, onSuccess?: (data: any) => void, onError?: (error: any) => void) => void;
    refresh?: () => void;
    funcActivateCard?: (cardId: string, onSuccess?: (data: any) => void, onError?: (error: any) => void) => void;
    funcCancelCard?: (data: any, onSuccess?: (data: any) => void, onError?: (error: any) => void) => void;
    funcSuspendCard?: (cardId: string, onSuccess?: (data: any) => void, onError?: (error: any) => void) => void;
    funcUnsuspendCard?: (cardId: string, onSuccess?: (data: any) => void, onError?: (error: any) => void) => void;
    funcUpdateCardPhoneNumberCard?: (
      cardId: string,
      phoneNumber: phoneNumberProp,
      onSuccess?: (data: any) => void,
      onError?: (error: any) => void
    ) => void;
  };
  [AccountManagementStackRoutes.CardDetailInfo]: {
    account: string;
    tab?: number;
    record?: Card;
  };
  [AccountManagementStackRoutes.ViewAllCard]: undefined;
  [AccountManagementStackRoutes.LockCard]: {
    account: string;
    tab?: number;
    record?: Card;
    func?: (cardId: string, onSuccess?: (data: any) => void, onError?: (error: any) => void) => void;
    actionResponse?: any;
    actionError?: any;
    refresh?: () => void;
  };
  [AccountManagementStackRoutes.SetCardLimit]: {
    account: string;
    tab?: number;
    record?: CardWithLimitProp;
    func?: (
      cardId: string,
      atmLimit?: number,
      purchaseLimit?: number,
      onSuccess?: (data: any) => void,
      onError?: (error: any) => void
    ) => void;
    actionResponse?: any;
    actionError?: any;
    refresh?: () => void;
  };
  [AccountManagementStackRoutes.ChangeATMPIN]: {
    account: string;
    tab?: number;
    record?: Card;
    func?: (cardId: string, pin: string, onSuccess?: (data: any) => void, onError?: (error: any) => void) => void;
    actionResponse?: any;
    actionError?: any;
  };
  [AccountManagementStackRoutes.ReplaceCard]: undefined;
  [AccountManagementStackRoutes.UpdateMobileNo]: {
    tab?: number;
    record?: any;
    func?: (cardId: string, phoneNumber: phoneNumberProp, onSuccess?: (data: any) => void, onError?: (error: any) => void) => void;
    refresh?: () => void;
  };
  [AccountManagementStackRoutes.ListBottomSheetFilter]: ListBottomSheetFilterProps;
  [AccountManagementStackRoutes.BottomSheetDatePicker]: BottomSheetDatePickerProps;
  [AccountManagementStackRoutes.AccountTransactionHistory]: { account: string };
  [AccountManagementStackRoutes.AccountTransactionDetail]: { record: any };
  [AccountManagementStackRoutes.CardTransactionHistory]: { account: string };
  [AccountManagementStackRoutes.TopUp]: {
    record?: any;
    func?: (
      accountId: string,
      amount: number,
      currency: string,
      xmetadata: Xmetadata,
      onSuccess?: (data: any) => void,
      onError?: (error: any) => void
    ) => void;
    refresh?: () => void;
  };
  [AccountManagementStackRoutes.Withdraw]: {
    record?: any;
    func?: (
      accountId: string,
      amount: number,
      currency: string,
      xmetadata: Xmetadata,
      onSuccess?: (data: any) => void,
      onError?: (error: any) => void
    ) => void;
    refresh?: () => void;
  };
  [EarnStackRoutes.AccountSelect]: {
    asset?: string;
    value?: string;
    disabledForwardBrowser?: boolean;
    onSelect: (walletId: string) => void;
  };
  [AccountManagementStackRoutes.NewAccount]: {
    func?: (data: any, onSuccess?: (data: any) => void, onError?: (error: any) => void) => void;
    refresh?: () => void;
    onChangeView?: () => void;
  };
  [AccountManagementStackRoutes.IssueCard]: {
    account: string;
    tab?: number;
    record?: any;
    func?: (data: any, onSuccess?: (data: any) => void, onError?: (error: any) => void) => void;
    refresh?: () => void;
  };
  [AccountManagementStackRoutes.CardActivation]: {
    account: string;
    tab?: number;
    record?: Card;
    func?: (cardId: string, onSuccess?: (data: any) => void, onError?: (error: any) => void) => void;
    actionResponse?: any;
    actionError?: any;
    refresh?: () => void;
  };
  [AccountManagementStackRoutes.SuspendCard]: {
    account: string;
    tab?: number;
    record?: Card;
    func?: (cardId: string, onSuccess?: (data: any) => void, onError?: (error: any) => void) => void;
    actionResponse?: any;
    actionError?: any;
    refresh?: () => void;
  };
  [AccountManagementStackRoutes.CancelCard]: {
    account: string;
    tab?: number;
    record?: Card;
    func?: (data: any, onSuccess?: (data: any) => void, onError?: (error: any) => void) => void;
    actionResponse?: any;
    actionError?: any;
    refresh?: () => void;
  };
  [AccountManagementStackRoutes.UnionPay]: BillPaymentUnionPay;
  [AccountManagementStackRoutes.DigitalDeposit]: {
    account: string;
    record?: any;
    refresh?: () => void;
  };
  [AccountManagementStackRoutes.AccountsTransfers]: {
    account: string;
    tab?: number;
    record?: any;
    func?: (data: any, onSuccess?: (data: any) => void, onError?: (error: any) => void) => void;
    refresh?: () => void;
  };

  [AccountManagementStackRoutes.DownloadTransactionReport]: {
    walletId?: string;
    creditLineId?: string;
  };
  [AccountManagementStackRoutes.Xmetadata]: {
    xmetadata: { [key: string]: any };
  };
  [AccountManagementStackRoutes.FixedLimit]: {
    record?: any;
    func?: (limit: number, onSuccess?: (data: any) => void, onError?: (error: any) => void) => void;
    refresh?: () => void;
  };
  [AccountManagementStackRoutes.ViewKycInfo]: {
    data: any;
    func?: (limit: number, onSuccess?: (data: any) => void, onError?: (error: any) => void) => void;
    refresh?: () => void;
  };
  [AccountManagementStackRoutes.UpdateKycInfo]: {
    data: any;
    func?: (limit: number, onSuccess?: (data: any) => void, onError?: (error: any) => void) => void;
    refresh?: () => void;
  };
};

export type AccountManagementStackScreenProps<Screen extends keyof AccountManagementStackParamList> = NativeStackScreenProps<
  AccountManagementStackParamList,
  Screen
>;

const AccountManagementStack = createNativeStackNavigator<AccountManagementStackParamList>();

export function AccountManagementNavigator(): JSX.Element {
  const layout = useContext(LayoutContext);
  return (
    <AccountManagementStack.Navigator
      screenOptions={{
        headerShown: false
      }}
      initialRouteName={AccountManagementStackRoutes.List}>
      <AccountManagementStack.Screen name={AccountManagementStackRoutes.List} component={AccountListScreen} />
      <AccountManagementStack.Screen name={AccountManagementStackRoutes.Details} component={AccountDetailsNavigator} />
      <AccountManagementStack.Screen
        name={AccountManagementStackRoutes.CardDetail}
        component={CardDetailNavigator}
        options={{ presentation: 'transparentModal' }}
      />
      <AccountManagementStack.Screen
        name={AccountManagementStackRoutes.CardDetailInfo}
        component={CardDetailInfoWebScreen}
        options={{ presentation: 'transparentModal' }}
      />
      <AccountManagementStack.Screen
        name={AccountManagementStackRoutes.ViewAllCard}
        component={ViewAllCardScreen}
        options={{ presentation: 'transparentModal' }}
      />
      <AccountManagementStack.Screen
        name={AccountManagementStackRoutes.LockCard}
        component={LockCardNavigator}
        options={{ presentation: 'transparentModal' }}
      />
      <AccountManagementStack.Screen
        name={AccountManagementStackRoutes.SetCardLimit}
        component={SetCardLimitNavigator}
        options={{ presentation: 'transparentModal' }}
      />
      <AccountManagementStack.Screen
        name={AccountManagementStackRoutes.ChangeATMPIN}
        component={ChangeATMPINNavigator}
        options={{ presentation: 'transparentModal' }}
      />
      <AccountManagementStack.Screen
        name={AccountManagementStackRoutes.ReplaceCard}
        component={ReplaceCardScreen}
        options={{ presentation: 'transparentModal' }}
      />
      <AccountManagementStack.Screen
        name={AccountManagementStackRoutes.UpdateMobileNo}
        component={UpdateMobileNoNavigator}
        options={{ presentation: 'transparentModal' }}
      />
      <AccountManagementStack.Screen
        name={AccountManagementStackRoutes.ListBottomSheetFilter}
        component={ListBottomSheetFilter}
        options={{ presentation: 'transparentModal' }}
      />
      <AccountManagementStack.Screen
        name={AccountManagementStackRoutes.BottomSheetDatePicker}
        component={BottomSheetDatePicker}
        options={{ presentation: 'transparentModal' }}
      />
      <AccountManagementStack.Screen
        name={AccountManagementStackRoutes.AccountTransactionHistory}
        component={AccountTransactionHistoryScreen}
      />
      <AccountManagementStack.Screen
        name={AccountManagementStackRoutes.AccountTransactionDetail}
        component={AccountTransactionDetailScreen}
      />
      <AccountManagementStack.Screen name={AccountManagementStackRoutes.CardTransactionHistory} component={CardTransactionHistoryScreen} />
      <AccountManagementStack.Screen
        options={{
          presentation: 'transparentModal',
          headerShown: false
        }}
        name={AccountManagementStackRoutes.TopUp}
        component={TopUpNavigator}
      />
      <AccountManagementStack.Screen
        options={{
          presentation: 'transparentModal',
          headerShown: false
        }}
        name={AccountManagementStackRoutes.Withdraw}
        component={WithdrawNavigator}
      />
      <AccountManagementStack.Screen
        name={EarnStackRoutes.AccountSelect}
        component={AccountSelectorNativeModal}
        options={{ presentation: 'transparentModal' }}
      />
      <AccountManagementStack.Screen
        name={AccountManagementStackRoutes.NewAccount}
        component={NewAccountNavigator}
        options={{ presentation: 'transparentModal' }}
      />
      <AccountManagementStack.Screen
        name={AccountManagementStackRoutes.IssueCard}
        component={IssueCardNavigator}
        options={{ presentation: 'transparentModal' }}
      />
      <AccountManagementStack.Screen
        name={AccountManagementStackRoutes.CardActivation}
        component={CardActivationNavigator}
        options={{ presentation: 'transparentModal' }}
      />
      <AccountManagementStack.Screen
        name={AccountManagementStackRoutes.CancelCard}
        component={CancelCardNavigator}
        options={{ presentation: 'transparentModal' }}
      />
      <AccountManagementStack.Screen
        name={AccountManagementStackRoutes.SuspendCard}
        component={SuspendCardNavigator}
        options={{ presentation: 'transparentModal' }}
      />
      <AccountManagementStack.Screen
        name={AccountManagementStackRoutes.UnionPay}
        component={UnionPayNavigator}
        options={{ presentation: 'transparentModal' }}
      />
      <AccountManagementStack.Screen
        name={AccountManagementStackRoutes.DigitalDeposit}
        component={DigitalDepositNavigator}
        options={{ presentation: 'transparentModal' }}
      />
      <AccountManagementStack.Screen
        name={AccountManagementStackRoutes.AccountsTransfers}
        component={AccountsTransfersNavigator}
        options={{ presentation: 'transparentModal' }}
      />
      <AccountManagementStack.Screen
        name={AccountManagementStackRoutes.DownloadTransactionReport}
        component={DownloadTransactionReportNavigator}
        options={{ presentation: 'transparentModal' }}
      />
      <AccountManagementStack.Screen
        name={AccountManagementStackRoutes.Xmetadata}
        component={TransactionInfoScreen}
        options={{ presentation: 'transparentModal' }}
      />
      <AccountManagementStack.Screen
        options={{
          presentation: 'transparentModal',
          headerShown: false
        }}
        name={AccountManagementStackRoutes.FixedLimit}
        component={FixedLimitNavigator}
      />
      <AccountManagementStack.Screen
        options={{
          presentation: 'transparentModal',
          headerShown: false
        }}
        name={AccountManagementStackRoutes.UpdateKycInfo}
        component={UpdateCardAccountKycInfo}
      />
    </AccountManagementStack.Navigator>
  );
}
